import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import UserMenu from "../components/UserMenu";
import StartBuildingButton, { BuildASkillButton } from "../components/StartBuildingButton";
import DownloadPdfButton from "../components/DownloadPdfButton";
import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";
import thePathContent from "../shared/the_path_content.json";

// Per-skill left-border accents, drawn from the brand palette so the
// cards read as a single family rather than seven competing accents.
//
// Content extracted (Jul 12 2026) to `/app/shared/the_path_content.json`
// so the backend PDF renderer reads the same array — zero drift. The
// on-screen layout, ordering, accents, and copy are unchanged from
// pre-extraction; render is byte-identical.
const SKILLS = thePathContent.skills;

const SectionKicker = ({ children, testid }) => (
  <p
    data-testid={testid}
    className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70"
  >
    {children}
  </p>
);

const SkillCard = ({ skill, idx }) => (
  <article
    data-testid={`path-skill-card-${idx}`}
    className="ae-card p-6 md:p-7 shadow-sm hover:shadow-md transition-shadow duration-300 border-l-[3px]"
    style={{ borderLeftColor: skill.accent }}
  >
    <h3 className="font-extrabold text-xl text-[#1c2d3d] tracking-tight">
      {skill.name}
    </h3>
    <p className="mt-3 text-[#4a6984]/85 font-medium leading-relaxed">
      {skill.desc}
    </p>
    <div className="mt-5 space-y-2.5">
      <p className="text-xs">
        <span className="font-bold uppercase tracking-[0.15em] text-[#4a6984]/70">
          Learning Skill:
        </span>{" "}
        <span className="font-extrabold text-[#1c2d3d] uppercase tracking-[0.1em]">
          {skill.learning}
        </span>
      </p>
      <p className="text-[15px] text-[#4a6984] font-medium leading-relaxed">
        <span className="font-bold uppercase tracking-[0.15em] text-[#4a6984]/70 text-xs">
          Where it leads:
        </span>{" "}
        {skill.leads}
      </p>
    </div>
  </article>
);

const ThePath = () => {
  const { user } = useAuth();

  return (
    <div
      className="min-h-screen bg-[#f4f5f7] text-[#4a6984]"
      data-testid="the-path-page"
    >
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#1c2d3d]/95 border-b border-[#1c2d3d] shadow-lg shadow-[#1c2d3d]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link
            to="/"
            data-testid="brand-logo"
            className="font-black text-base sm:text-lg tracking-tight text-white/80 hover:text-white inline-flex items-center gap-2 whitespace-nowrap shrink-0"
          >
            <img
              src="/brand/atomicedu-logo-mark.png"
              alt="Atomic Edu"
              className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0"
            />
            Atomic Edu
          </Link>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <span className="hidden md:inline-flex">
              <StartBuildingButton />
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      <main
        data-testid="the-path-main"
        className="pt-32 pb-24 max-w-[760px] mx-auto px-6"
      >
        {/* SECTION 1 — Page header */}
        <section data-testid="path-header">
          <SectionKicker testid="path-header-kicker">
            From your classroom to their future
          </SectionKicker>
          <h1
            data-testid="path-headline"
            className="mt-3 font-black text-4xl sm:text-5xl text-[#1c2d3d] tracking-tight leading-[1.05]"
          >
            Here&apos;s how the thread runs.
          </h1>
          <p
            data-testid="path-subheading"
            className="mt-5 text-[#4a6984]/85 font-medium text-base sm:text-lg leading-relaxed"
          >
            Every transferable skill your students practise today connects to a
            Learning Skill on their report card — and to a real path they will
            walk for the rest of their lives. Here is what that thread looks
            like, from Grade 4 to adulthood.
          </p>
        </section>

        {/* SECTION 2 — Story block */}
        <section data-testid="path-story" className="mt-16">
          <div
            className="ae-card rounded-lg border-l-[3px] px-6 py-7 md:px-8 md:py-8 shadow-sm"
            style={{ borderLeftColor: "#96ded1" }}
          >
            <p
              data-testid="path-story-p1"
              className="text-[#4a6984] font-medium text-base md:text-lg leading-relaxed"
            >
              A Grade 6 student learns to listen before speaking in a class
              debate. That&apos;s Collaboration — one of Ontario&apos;s seven
              transferable skills. It maps to{" "}
              <span className="font-extrabold text-[#1c2d3d]">
                Teamwork &amp; Cooperation
              </span>{" "}
              — one of the six Learning Skills reported on every Ontario report
              card. It shows up at 22 when their manager says: &ldquo;She&apos;s
              the one who actually listens before she talks.&rdquo; It shows up
              at 35 when their team ships something hard because they stayed in
              the room together.
            </p>
            <p
              data-testid="path-story-p2"
              className="mt-5 text-[#4a6984] font-medium text-base md:text-lg leading-relaxed"
            >
              That thread starts in your classroom. Every lesson is a chance to
              pull it tighter.
            </p>
            <p
              data-testid="path-story-pivot"
              className="mt-6 text-lg md:text-xl font-extrabold text-[#1c2d3d] leading-snug"
            >
              Atomic Edu builds the thread in — automatically, every time.
            </p>
          </div>
        </section>

        {/* SECTION 2.5 — Career Connection (RELOCATED from homepage
            Feb 2026, Lane 1). Same exact copy as the retired
            `career-connection-section`. This concrete example bridges
            the abstract story block above with the seven-skill thread
            below. */}
        <section
          data-testid="path-career-connection"
          className="mt-16"
        >
          <SectionKicker testid="path-career-connection-kicker">
            What it looks like in a lesson
          </SectionKicker>
          <article
            data-testid="career-connection-card"
            className="mt-6 ae-card border-l-[3px] border-l-[#96ded1] shadow-sm p-6 md:p-7"
          >
            <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
              Career Connection
            </p>
            <p
              data-testid="career-connection-quote"
              className="mt-4 text-[#4a6984] font-medium leading-relaxed text-base md:text-lg"
            >
              &ldquo;Critical thinking is what nurses use when triaging patients — they don&apos;t have time to look everything up, so they have to think through what they know. Engineers use it when diagnosing why a structure failed. Your students are practising that same skill today.&rdquo;
            </p>
            <span
              data-testid="career-connection-skill-tag"
              className="mt-5 inline-block rounded-md bg-[#1c2d3d] text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em]"
            >
              Critical Thinking &amp; Problem Solving
            </span>
          </article>

          <p
            data-testid="career-connection-closing"
            className="mt-8 text-center text-[#1c2d3d] font-semibold text-base md:text-lg leading-relaxed"
          >
            Every lesson. Every grade. Every subject. Students don&apos;t just learn content —{" "}
            <span className="font-extrabold text-[#1c2d3d] border-b-2 border-[#96ded1] pb-0.5">
              they learn what the content is for.
            </span>
          </p>

          <div
            data-testid="career-connection-cta-wrap"
            className="mt-10 flex justify-center"
          >
            <BuildASkillButton
              testid="career-connection-build-a-skill"
              className="h-12 px-6 text-base"
            />
          </div>
        </section>

        {/* SECTION 3 — Full thread */}
        <section data-testid="path-thread" className="mt-16">
          <SectionKicker testid="path-thread-kicker">
            The seven skills — and where they lead
          </SectionKicker>
          <p
            data-testid="path-thread-intro"
            className="mt-3 text-[#4a6984]/80 font-medium leading-relaxed"
          >
            Each of Ontario&apos;s seven transferable skills maps to a Learning
            Skill on the report card and to real outcomes beyond school. Here is
            the full thread.
          </p>

          <div
            data-testid="path-thread-grid"
            className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            {SKILLS.slice(0, 6).map((skill, idx) => (
              <SkillCard key={skill.name} skill={skill} idx={idx} />
            ))}
          </div>
          {/* Seventh card — full-width at the bottom */}
          <div className="mt-5">
            <SkillCard skill={SKILLS[6]} idx={6} />
          </div>
        </section>

        {/* SECTION 4 — Closing statement */}
        <section data-testid="path-closing" className="mt-20 text-center">
          <p
            data-testid="path-closing-copy"
            className="text-[#1c2d3d] font-semibold text-lg md:text-xl leading-relaxed max-w-[640px] mx-auto"
          >
            Every lesson is a thread. Every skill practised is a pull in the
            right direction.
            <br />
            The path doesn&apos;t start at graduation. It starts in your
            classroom — today.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              data-testid={user ? "path-cta-generate" : "path-cta-start"}
              className="ae-cta h-12 px-6 rounded-lg text-sm font-bold inline-flex items-center gap-2"
            >
              <Link to={user ? "/generator" : "/generator#skills-generator"}>
                {user ? "Generate a lesson" : "Try for free"}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
            <DownloadPdfButton
              href={`${API}/the-path/pdf`}
              testId="path-download-pdf"
              label="Download The Path (PDF)"
              size="lg"
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default ThePath;
